/**
 * email-signup D5 — the email signup box on a studio's custom website: an email field, Subscribe, and the
 * consent line ending in Privacy; after a signup, the thank-you line in its place. The visitor's address joins
 * the studio's Email list, which the studio downloads into its own email service; Telify sends nothing to it.
 * A site places it under its own heading and line (Dana's footer: "Letters from Dana").
 *
 * `standard-only`: a site restyles it through its parts, states and tokens. A custom drawing has no focus or
 * input bindings, and the box needs both (the ticket is read on first focus).
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'email-signup',
  description: "An email signup box: the visitor's email and Subscribe, with the consent line and Privacy. The address joins the studio's Email list.",
  placeable: true,
  drawing: 'standard-only',
  options: {
    placeholder: { type: 'string', default: 'Your email', maxLength: 40, description: "The email field's placeholder." },
    buttonLabel: { type: 'string', default: 'Subscribe', maxLength: 30, description: 'The button text.' },
    consent: {
      type: 'string',
      default: 'By subscribing you agree to get emails from {studio}. You can unsubscribe at any time.',
      maxLength: 200,
      description: "The consent line under the box, always followed by the Privacy link; {studio} is the studio's name. These words are stored with each signup.",
    },
    done: { type: 'string', default: "You're on the list.", maxLength: 120, description: 'The line that replaces the box after a signup.' },
  },
  parts: {
    form: { description: 'The whole box: the field, the button and the consent line.', required: false },
    email: { description: 'The email field.', required: true },
    submit: { description: 'Subscribe.', required: true, states: ['disabled'], role: 'primary-action' },
    consent: { description: 'The consent line under the box.', required: true },
    privacy: { description: "The Privacy link at the end of the consent line, to Telify's privacy policy on the site's own address.", required: true },
    done: { description: 'The line that replaces the box after a signup.', required: false },
    error: { description: 'The line when the address was refused or the signup could not go through yet; the box stays.', required: false },
  },
  actions: {
    submit: {
      description: "Adds the address to the studio's Email list.",
      args: [{ name: 'email', kind: 'value', type: 'string', format: 'text', maxLength: 254 }],
    },
  },
  events: {
    subscribed: 'The visitor joined the email list.',
  },
});

/** What the email signup box knows. */
export interface EmailSignupState {
  /** form while the box shows; sending while Subscribe waits and goes; done after a signup. */
  readonly status: 'form' | 'sending' | 'done';
  /** Why the address or the signup was refused, or null. The box stays, with what was typed. */
  readonly error: string | null;
}

/** What a drawing can ask the email signup box to do. */
export interface EmailSignupActions {
  /** Add the address to the studio's Email list. */
  readonly submit: (email: string) => void;
}

/** What the email signup box tells its host. */
export interface EmailSignupEvents {
  /** The visitor joined the email list. */
  readonly subscribed: Record<string, never>;
}
